import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  useApproveTransfer,
  useRejectTransfer,
  useTransfers,
  type TransferRequest,
} from '@/hooks/data/useAdmin';
import { fetchMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/admin/transfers')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile.role !== 'admin') throw redirect({ to: '/' });
  },
  component: AdminTransfersPage,
});

const STATUS_VARIANTS: Record<
  TransferRequest['status'],
  'default' | 'secondary' | 'success' | 'destructive' | 'warning'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'destructive',
  cancelled: 'secondary',
};

function AdminTransfersPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TransferRequest['status']>('pending');
  const { data: items = [], isLoading } = useTransfers(status);
  const approve = useApproveTransfer();
  const reject = useRejectTransfer();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  return (
    <section className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">
          {t('admin.transfers.title', 'Eigentums-Transfers')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('admin.transfers.subtitle', 'Anträge auf Übertragung von Klienten/Immobilien.')}
        </p>
      </header>

      <div className="flex gap-2">
        {(['pending', 'approved', 'rejected'] as const).map((s) => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Lädt …</p>}
        {!isLoading && items.length === 0 && (
          <p className="text-sm text-muted-foreground">Keine Anträge.</p>
        )}
        {items.map((r) => (
          <div
            key={r.id}
            className="rounded-2xl border border-border bg-card p-4 shadow-soft"
          >
            <div className="mb-2 flex items-center justify-between">
              <Badge variant={STATUS_VARIANTS[r.status]}>{r.status}</Badge>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(new Date(r.created_at))}
              </span>
            </div>
            <p className="text-sm">
              <span className="font-medium">
                {r.resource_type === 'client' ? t('admin.transfers.resource_client', 'Klient') : t('admin.transfers.resource_property', 'Immobilie')} ({r.resource_id.slice(0, 8)}
                )
              </span>{' '}
              · {r.reason}
            </p>
            {r.review_notes && (
              <p className="mt-1 text-xs italic text-muted-foreground">
                Begründung: {r.review_notes}
              </p>
            )}
            {r.status === 'pending' && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <Button
                  size="sm"
                  onClick={() => approve.mutate({ id: r.id })}
                  className="gap-1"
                >
                  <Check className="h-3.5 w-3.5" /> Genehmigen
                </Button>
                {rejectingId === r.id ? (
                  <div className="flex flex-1 flex-wrap items-end gap-2">
                    <Textarea
                      rows={1}
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      placeholder={t('admin.transfers.reject_reason_placeholder', 'Begründung für Ablehnung')}
                      className="min-h-9 flex-1"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        if (rejectNote.trim()) {
                          reject.mutate({ id: r.id, notes: rejectNote });
                          setRejectingId(null);
                          setRejectNote('');
                        }
                      }}
                    >
                      Bestätigen
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRejectingId(null);
                        setRejectNote('');
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejectingId(r.id)}
                    className="gap-1"
                  >
                    <X className="h-3.5 w-3.5" /> Ablehnen
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
